import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import {
  EyeIcon as Eye,
  EyeSlashIcon as EyeOff,
  ArrowDownOnSquareIcon as Save,
  UserIcon as User,
  EnvelopeIcon as Mail,
  LockClosedIcon as Lock,
  BuildingOfficeIcon as Building,
  ShieldCheckIcon as Shield,
  DevicePhoneMobileIcon as Smartphone,
  KeyIcon as Key,
} from '@heroicons/react/24/outline';
import { TwoFactorSetup } from '../components/Auth';

interface ProfileFormData {
  full_name: string;
  username: string;
  email: string;
  current_password: string;
  new_password: string;
  confirm_password: string;
}

const Profile: React.FC = () => {
  const { user, updateProfile, disable2FA } = useAuth();
  const [show2FASetup, setShow2FASetup] = useState(false);
  const [twoFALoading, setTwoFALoading] = useState(false);
  const [loading, setLoading] = useState(false);
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  const [formData, setFormData] = useState<ProfileFormData>({
    full_name: '',
    username: '',
    email: '',
    current_password: '',
    new_password: '',
    confirm_password: '',
  });

  useEffect(() => {
    if (user) {
      setFormData(prev => ({
        ...prev,
        full_name: user.full_name || '',
        username: user.username || '',
        email: user.email || '',
      }));
    }
  }, [user]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setMessage(null);

    try {
      // Validações
      if (formData.new_password && formData.new_password !== formData.confirm_password) {
        throw new Error('As senhas não coincidem');
      }

      if (formData.new_password && formData.new_password.length < 6) {
        throw new Error('A nova senha deve ter pelo menos 6 caracteres');
      }

      // Preparar dados para envio
      const updateData: any = {
        full_name: formData.full_name,
        username: formData.username,
        email: formData.email,
      };

      // Se está alterando senha, incluir senhas
      if (formData.new_password) {
        updateData.current_password = formData.current_password;
        updateData.new_password = formData.new_password;
      }

      await updateProfile(updateData);

      setMessage({ type: 'success', text: 'Perfil atualizado com sucesso!' });

      // Limpar campos de senha
      setFormData(prev => ({
        ...prev,
        current_password: '',
        new_password: '',
        confirm_password: '',
      }));
    } catch (error: any) {
      setMessage({
        type: 'error',
        text: error.message || 'Erro ao atualizar perfil',
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className='max-w-4xl mx-auto p-6'>
      <div className='mb-8'>
        <h1 className='text-3xl font-bold text-gray-900 dark:text-gray-100'>Gerenciar Conta</h1>
        <p className='mt-2 text-gray-600'>Atualize suas informações pessoais e configurações de segurança</p>
      </div>

      {message && (
        <div
          className={`mb-6 p-4 rounded-lg ${
            message.type === 'success'
              ? 'bg-green-50 text-green-800 border border-green-200'
              : 'bg-red-50 text-red-800 border border-red-200'
          }`}
        >
          {message.text}
        </div>
      )}

      <div className='grid grid-cols-1 lg:grid-cols-3 gap-8'>
        {/* Informações do Usuário */}
        <div className='lg:col-span-1'>
          <div className='bg-white dark:bg-gray-800 rounded-lg shadow border dark:border-gray-700 p-6'>
            <div className='text-center'>
              <div className='mx-auto h-24 w-24 bg-gradient-to-r from-primary-600 to-secondary-600 rounded-full flex items-center justify-center mb-4'>
                <User className='h-12 w-12 text-white' />
              </div>
              <h3 className='text-lg font-semibold text-gray-900 dark:text-white'>
                {user?.full_name || user?.username}
              </h3>
              <p className='text-gray-600'>{user?.email}</p>
              <div className='mt-4 flex items-center justify-center text-sm text-gray-500'>
                <Building className='h-4 w-4 mr-1' />
                {user?.company?.name || 'Empresa não definida'}
              </div>
            </div>
          </div>
        </div>

        {/* Formulário de Edição */}
        <div className='lg:col-span-2'>
          <form onSubmit={handleSubmit} className='space-y-6'>
            {/* Informações Pessoais */}
            <div className='bg-white dark:bg-gray-800 rounded-lg shadow border dark:border-gray-700 p-6'>
              <h3 className='text-lg font-semibold text-gray-900 dark:text-white mb-4'>Informações Pessoais</h3>

              <div className='grid grid-cols-1 md:grid-cols-2 gap-4'>
                <div>
                  <label className='block text-sm font-medium text-gray-700 mb-1'>Nome Completo</label>
                  <div className='relative'>
                    <User className='absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400' />
                    <input
                      type='text'
                      name='full_name'
                      value={formData.full_name}
                      onChange={handleInputChange}
                      className='pl-10 w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500'
                      placeholder='Seu nome completo'
                    />
                  </div>
                </div>

                <div>
                  <label className='block text-sm font-medium text-gray-700 mb-1'>Nome de Usuário</label>
                  <div className='relative'>
                    <User className='absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400' />
                    <input
                      type='text'
                      name='username'
                      value={formData.username}
                      onChange={handleInputChange}
                      className='pl-10 w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500'
                      placeholder='Seu nome de usuário'
                    />
                  </div>
                </div>

                <div className='md:col-span-2'>
                  <label className='block text-sm font-medium text-gray-700 mb-1'>E-mail</label>
                  <div className='relative'>
                    <Mail className='absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400' />
                    <input
                      type='email'
                      name='email'
                      value={formData.email}
                      onChange={handleInputChange}
                      className='pl-10 w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500'
                      placeholder='seu@email.com'
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* Alteração de Senha */}
            <div className='bg-white dark:bg-gray-800 rounded-lg shadow border dark:border-gray-700 p-6'>
              <h3 className='text-lg font-semibold text-gray-900 dark:text-white mb-4'>Alterar Senha</h3>
              <p className='text-sm text-gray-600 mb-4'>Deixe os campos em branco se não quiser alterar a senha</p>

              <div className='space-y-4'>
                <div>
                  <label className='block text-sm font-medium text-gray-700 mb-1'>Senha Atual</label>
                  <div className='relative'>
                    <Lock className='absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400' />
                    <input
                      type={showCurrentPassword ? 'text' : 'password'}
                      name='current_password'
                      value={formData.current_password}
                      onChange={handleInputChange}
                      className='pl-10 pr-10 w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500'
                      placeholder='Digite sua senha atual'
                    />
                    <button
                      type='button'
                      onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                      className='absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600'
                    >
                      {showCurrentPassword ? <EyeOff className='h-4 w-4' /> : <Eye className='h-4 w-4' />}
                    </button>
                  </div>
                </div>

                <div>
                  <label className='block text-sm font-medium text-gray-700 mb-1'>Nova Senha</label>
                  <div className='relative'>
                    <Lock className='absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400' />
                    <input
                      type={showNewPassword ? 'text' : 'password'}
                      name='new_password'
                      value={formData.new_password}
                      onChange={handleInputChange}
                      className='pl-10 pr-10 w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500'
                      placeholder='Digite a nova senha'
                    />
                    <button
                      type='button'
                      onClick={() => setShowNewPassword(!showNewPassword)}
                      className='absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600'
                    >
                      {showNewPassword ? <EyeOff className='h-4 w-4' /> : <Eye className='h-4 w-4' />}
                    </button>
                  </div>
                </div>

                <div>
                  <label className='block text-sm font-medium text-gray-700 mb-1'>Confirmar Nova Senha</label>
                  <div className='relative'>
                    <Lock className='absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400' />
                    <input
                      type={showConfirmPassword ? 'text' : 'password'}
                      name='confirm_password'
                      value={formData.confirm_password}
                      onChange={handleInputChange}
                      className='pl-10 pr-10 w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500'
                      placeholder='Confirme a nova senha'
                    />
                    <button
                      type='button'
                      onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                      className='absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600'
                    >
                      {showConfirmPassword ? <EyeOff className='h-4 w-4' /> : <Eye className='h-4 w-4' />}
                    </button>
                  </div>
                </div>
              </div>
            </div>

            {/* Autenticação de Dois Fatores */}
            <div className='bg-white dark:bg-gray-800 rounded-lg shadow border dark:border-gray-700 p-6'>
              <div className='flex items-center justify-between mb-4'>
                <div>
                  <h3 className='text-lg font-semibold text-gray-900 dark:text-white flex items-center gap-2'>
                    <Shield className='h-5 w-5' />
                    Autenticação de Dois Fatores (2FA)
                  </h3>
                  <p className='text-sm text-gray-600 mt-1'>Adicione uma camada extra de segurança à sua conta</p>
                </div>
                <div
                  className={`px-3 py-1 rounded-full text-xs font-medium ${
                    user?.twofa_enabled ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'
                  }`}
                >
                  {user?.twofa_enabled ? 'Ativado' : 'Desativado'}
                </div>
              </div>

              <div className='space-y-4'>
                {!user?.twofa_enabled ? (
                  <div className='border border-gray-200 rounded-lg p-4'>
                    <div className='flex items-start gap-3'>
                      <Smartphone className='h-5 w-5 text-blue-600 mt-0.5' />
                      <div className='flex-1'>
                        <h4 className='font-medium text-gray-900 dark:text-white'>Configurar 2FA</h4>
                        <p className='text-sm text-gray-600 mt-1'>
                          Use um aplicativo autenticador como Google Authenticator ou Authy para gerar códigos de
                          verificação.
                        </p>
                        <button
                          type='button'
                          onClick={() => setShow2FASetup(true)}
                          disabled={twoFALoading}
                          className='mt-3 inline-flex items-center gap-2 bg-blue-600 hover:bg-blue-700 disabled:bg-gray-400 text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors'
                        >
                          {twoFALoading ? (
                            <div className='animate-spin rounded-full h-4 w-4 border-b-2 border-white'></div>
                          ) : (
                            <Key className='h-4 w-4' />
                          )}
                          {twoFALoading ? 'Configurando...' : 'Configurar 2FA'}
                        </button>
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className='border border-green-200 rounded-lg p-4 bg-green-50'>
                    <div className='flex items-start gap-3'>
                      <Shield className='h-5 w-5 text-green-600 mt-0.5' />
                      <div className='flex-1'>
                        <h4 className='font-medium text-gray-900 dark:text-white'>2FA Ativo</h4>
                        <p className='text-sm text-gray-600 mt-1'>
                          Sua conta está protegida com autenticação de dois fatores.
                        </p>
                        <button
                          type='button'
                          onClick={async () => {
                            const otp = prompt(
                              'Digite o código do seu aplicativo autenticador para desabilitar o 2FA:',
                            );
                            if (otp) {
                              setTwoFALoading(true);
                              try {
                                await disable2FA(otp);
                                setMessage({ type: 'success', text: '2FA desabilitado com sucesso!' });
                              } catch (error) {
                                setMessage({
                                  type: 'error',
                                  text: 'Erro ao desabilitar 2FA. Verifique o código e tente novamente.',
                                });
                              } finally {
                                setTwoFALoading(false);
                              }
                            }
                          }}
                          disabled={twoFALoading}
                          className='mt-3 inline-flex items-center gap-2 bg-red-600 hover:bg-red-700 disabled:bg-gray-400 text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors'
                        >
                          {twoFALoading ? (
                            <div className='animate-spin rounded-full h-4 w-4 border-b-2 border-white'></div>
                          ) : (
                            <Lock className='h-4 w-4' />
                          )}
                          {twoFALoading ? 'Desabilitando...' : 'Desabilitar 2FA'}
                        </button>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Botão de Salvar */}
            <div className='flex justify-end'>
              <button
                type='submit'
                disabled={loading}
                className='flex items-center gap-2 bg-primary-600 hover:bg-primary-700 disabled:bg-gray-400 text-white px-6 py-2 rounded-lg font-medium transition-colors'
              >
                {loading ? (
                  <div className='animate-spin rounded-full h-4 w-4 border-b-2 border-white'></div>
                ) : (
                  <Save className='h-4 w-4' />
                )}
                {loading ? 'Salvando...' : 'Salvar Alterações'}
              </button>
            </div>
          </form>
        </div>
      </div>

      {/* Modal de Configuração 2FA */}
      {show2FASetup && (
        <div className='fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50'>
          <div className='bg-white dark:bg-gray-800 rounded-lg max-w-md w-full max-h-[90vh] overflow-y-auto'>
            <div className='p-6'>
              <div className='flex items-center justify-between mb-4'>
                <h3 className='text-lg font-semibold text-gray-900 dark:text-white'>Configurar 2FA</h3>
                <button onClick={() => setShow2FASetup(false)} className='text-gray-400 hover:text-gray-600'>
                  <svg className='h-6 w-6' fill='none' viewBox='0 0 24 24' stroke='currentColor'>
                    <path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M6 18L18 6M6 6l12 12' />
                  </svg>
                </button>
              </div>
              <TwoFactorSetup
                onComplete={backupCodes => {
                  setShow2FASetup(false);
                  setMessage({
                    type: 'success',
                    text: '2FA configurado com sucesso! Guarde seus códigos de backup em local seguro.',
                  });
                }}
                onCancel={() => setShow2FASetup(false)}
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Profile;
